import React, { useCallback } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
    Text,
} from 'grommet';
import { useParams } from 'react-router-dom';
import {
    API,
    resolveSortField,
    RowsPerPageSelect,
    usePaginatedFetch,
    usePaginatedSort,
    Utils,
} from '@enrich/shared-lib';
import QueryString from 'qs';

export const ClosedJobs = () => {
    const { companySlug } = useParams();
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort({
        initialSort: { property: 'updatedAt', direction: 'desc' },
    });

    const fetchPage = useCallback(
        () => loadJobs({ page, pageSize, companySlug, sort }),
        [page, pageSize, companySlug, sort],
    );
    const { items: jobs } = usePaginatedFetch({
        fetchPage,
        applyPagination,
        transform: Utils.deepFlatten,
    });

    const dataColumns = [
        {
            header: 'Job Title',
            property: 'title',
            render: (job) => {
                return (
                    <Anchor
                        key={job.id}
                        href={`/${companySlug}/jobs/${job.id}`}
                        label={job.title}
                    />
                );
            },
            sortable: true,
        },
        {
            header: 'Company',
            property: 'company.name',
            render: (job) => {
                if (!job.company) return 'N/A';
                return job.company.name;
            },
            sortable: true,
        },
        {
            header: 'Seats',
            property: 'seats',
            sortable: true,
        },
        {
            header: 'Offers Accepted',
            property: 'offersAccepted',
            sortable: true,
            render: (job) => {
                const matches = job.candidacies ?? [];
                if (matches.length === 0) return 0;
                return (
                    matches.filter((x) => x.status === 'offer_accepted')
                        .length || 0
                );
            },
        },
        {
            header: 'Matches',
            property: 'matchCount',
            sortable: true,
            render: (job) => {
                return job.candidacies?.length || 0;
            },
        },
        {
            header: 'Last Updated',
            property: 'updatedAt',
            sortable: true,
            render: (job) => {
                return new Date(job.updatedAt).toLocaleString(undefined, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                });
            },
        },
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Box gap="small">
                    <Box
                        direction="row-responsive"
                        justify="between"
                        pad={{ right: 'small' }}>
                        <Heading level="2">Closed Jobs</Heading>
                    </Box>
                    <Box>
                        {jobs.length === 0 ? (
                            <Box pad="medium" align="center">
                                <Text>No closed jobs found</Text>
                            </Box>
                        ) : (
                            <DataTable
                                primaryKey="id"
                                sort={{ ...sort, external: true }}
                                onSort={handleSort}
                                data={jobs}
                                columns={dataColumns}
                            />
                        )}
                    </Box>
                    <Box
                        direction="row-responsive"
                        align="center"
                        justify="between"
                        gap="small"
                        pad="small">
                        <RowsPerPageSelect
                            options={stepOptions}
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        />
                        <Pagination
                            numberItems={totalItems}
                            page={page}
                            onChange={handlePageChange}
                            step={pageSize}
                            summary={true}
                        />
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

const SORT_FIELD_MAP = {
    title: 'title',
    'company.name': 'company.name',
    seats: 'seats',
    updatedAt: 'updatedAt',
    offersAccepted: 'offersAcceptedCount',
    matchCount: 'matchCount',
};

const loadJobs = async ({ page, pageSize, companySlug, sort } = {}) => {
    const filters = {
        status: 'closed',
    };

    if (companySlug) {
        filters.company = {
            slug: {
                $eqi: companySlug,
            },
        };
    }

    const query = QueryString.stringify(
        {
            filters,
            populate: {
                company: {
                    fields: ['id', 'name'],
                },
                candidacies: {
                    filters: {
                        status: {
                            $in: [
                                'interested',
                                'applied',
                                'assessment',
                                'interview',
                                'second_interview',
                                'offer_received',
                                'offer_accepted',
                            ],
                        },
                    },
                    fields: ['id', 'status'],
                },
            },
            pagination: page && pageSize ? { page, pageSize } : undefined,
            sort: [
                `${resolveSortField(sort, SORT_FIELD_MAP, 'updatedAt')}:${
                    sort?.direction ?? 'desc'
                }`,
            ],
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/jobs?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return { data: [], meta: { pagination: { page: 1, total: 0 } } };
};
